import { lazy, Suspense, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { AppHeader } from '../components/AppHeader';
import { AirCard } from '../components/cards/AirCard';
import { RainPressureCard } from '../components/cards/RainPressureCard';
import { SensorCard } from '../components/cards/SensorCard';
import { SunCard } from '../components/cards/SunCard';
import { WindCard } from '../components/cards/WindCard';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { useNow } from '../hooks/useNow';
import { buildWeatherView } from '../weather/format';
import { DEFAULT_RANGE, findRange, windowFromRange, type MetricKey } from '../weather/history';
import type { SectionKey } from '../weather/series';
import { useHistory } from '../weather/useHistory';
import { useWeatherFeed } from '../weather/WeatherProvider';

const COLUMN_GAP = 'clamp(14px, 1.6vw, 22px)';

/**
 * Sub lățimea asta cele trei coloane devin una singură.
 *
 * Coloanele trec pe `display: contents`, deci cardurile ajung copii direcți ai
 * șirului și le pot da o ordine proprie — altfel starea senzorului ar rămâne
 * lipită de „Aer”, a doua de sus, în loc să încheie pagina.
 */
const SINGLE_COLUMN = '@media (max-width: 900px)';

/**
 * Vederea de istoric aduce cu ea biblioteca de grafice, care e mai mare decât
 * tot restul aplicației. Cine deschide dashboardul ca să vadă temperatura nu
 * trebuie să o descarce; se aduce la prima apăsare pe un buton de istoric.
 */
const HistoryDetail = lazy(() =>
  import('../components/HistoryDetail').then((module) => ({ default: module.HistoryDetail })),
);

/** Dashboardul are nevoie doar de presiune, pentru trendul din card. */
const TREND_METRICS: MetricKey[] = ['pressure'];
/** Fereastra se reașază o dată la cinci minute, nu la fiecare secundă de ceas. */
const TREND_REFRESH_MS = 300_000;

const column = (flex: number) =>
  ({
    flex,
    display: 'flex',
    flexDirection: 'column',
    gap: COLUMN_GAP,
    minWidth: 300,
    [SINGLE_COLUMN]: { display: 'contents' },
  }) as const;

export function DashboardPage() {
  const { state, status, error } = useWeatherFeed();
  const now = useNow();

  // Secțiunea al cărei istoric e deschis; `null` = grila de carduri.
  const [detail, setDetail] = useState<SectionKey | null>(null);

  // Seria scurtă alimentează trendul de presiune din card.
  const slot = Math.floor(now.getTime() / TREND_REFRESH_MS);
  const trendWindow = useMemo(
    () => windowFromRange(findRange(DEFAULT_RANGE.key), slot * TREND_REFRESH_MS),
    [slot],
  );
  const { points } = useHistory(trendWindow, TREND_METRICS);

  const view = useMemo(
    () => buildWeatherView(state, now, getRuntimeConfig().stationName, points),
    [state, now, points],
  );

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.weather.bg,
        color: theme.palette.weather.ink,
        minHeight: '100vh',
        padding: 'clamp(18px, 3vw, 36px) clamp(16px, 3.5vw, 48px) 48px',
        transition: 'background-color .5s ease, color .5s ease',
      })}
    >
      <AppHeader view={view} connection={status} />

      {error !== null && status === 'error' && (
        <Box
          role="alert"
          sx={(theme) => ({
            marginBottom: COLUMN_GAP,
            padding: '12px 18px',
            borderRadius: `${theme.radii.tile}px`,
            backgroundColor: theme.palette.weather.accentSoft,
            color: theme.palette.weather.accent,
            fontSize: 13,
            fontWeight: 500,
          })}
        >
          {error}
        </Box>
      )}

      {detail !== null ? (
        <Suspense
          fallback={
            <Box sx={{ padding: '60px 0', display: 'grid', placeItems: 'center' }}>
              <CircularProgress
                size={26}
                sx={(theme) => ({ color: theme.palette.weather.accent })}
              />
            </Box>
          }
        >
          <HistoryDetail section={detail} onClose={() => setDetail(null)} />
        </Suspense>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: COLUMN_GAP,
            [SINGLE_COLUMN]: { flexDirection: 'column', alignItems: 'stretch' },
          }}
        >
          <Box
            sx={{
              ...column(1.7),
              // Aerul deschide pagina, starea senzorului o încheie.
              '& > *:nth-of-type(1)': { [SINGLE_COLUMN]: { order: 1 } },
              '& > *:nth-of-type(2)': { [SINGLE_COLUMN]: { order: 5 } },
            }}
          >
            <AirCard view={view} onOpenHistory={() => setDetail('aer')} />
            <SensorCard view={view} onOpenHistory={() => setDetail('senzor')} />
          </Box>

          <Box sx={{ ...column(1), '& > *': { [SINGLE_COLUMN]: { order: 2 } } }}>
            <WindCard view={view} onOpenHistory={() => setDetail('vant')} />
          </Box>

          <Box
            sx={{
              ...column(1),
              '& > *:nth-of-type(1)': { [SINGLE_COLUMN]: { order: 3 } },
              '& > *:nth-of-type(2)': { [SINGLE_COLUMN]: { order: 4 } },
            }}
          >
            <RainPressureCard view={view} onOpenHistory={() => setDetail('apa')} />
            <SunCard view={view} onOpenHistory={() => setDetail('soare')} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
